import { gql, useLazyQuery, useMutation } from "@apollo/client";
import PropTypes from "prop-types";
import { useContext, useEffect, useState } from "react";

// @mui material components
import {
  Card,
  CircularProgress,
  Fade,
  Grid,
  Icon,
  InputLabel,
  MenuItem,
  Modal,
  Select,
  Tooltip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Box,
} from "@mui/material";

import SoftBox from "components/SoftBox";
import SoftTypography from "components/SoftTypography";
import { MessageManager } from "context";
import SoftInput from "components/SoftInput";
import SoftButton from "components/SoftButton";
import { formatMoneyPunto } from "utils/formatters";
import SoftInputNumber from "components/SoftInputNumberUpgrade";
import AddFiles from "../ModalFiles";
import SinDatos from "components/Carteles/SinDatos";

const style = {
  position: "absolute",
  top: "50%",
  left: "50%",
  transform: "translate(-50%, -50%)",
  maxHeight: "90%",
  width: { xs: "90%", sm: "70%", xxl: "50%" },
  overflow: "auto",
  p: 3,
};

// GraphQL Queries
const GET_PROVEEDORES = gql`
  query GetProveedores {
    proveedores {
      id
      razonSocial
      nombre
      apellido
    }
  }
`;

const GET_MOVIMIENTOS = gql`
  query GetMovimientos($filter: JSON) {
    movimientos(filter: $filter) {
      id
      tipo
      ordenPago
      fechaEntrega
      movimientoDetalles {
        id
        cantidad
        precio
        materiaPrima {
          tipoUnidad {
            nombre
            multiplicador
          }
        }
      }
    }
  }
`;

const SAVE_FACTURA = gql`
  mutation SaveFactura($input: FacturaInput!) {
    saveFactura(input: $input) {
      id
    }
  }
`;

export default function ModalAddFactura({ open, handleClose, refetchParent }) {
  const [proveedor, setProveedor] = useState(null);
  const [movimientos, setMovimientos] = useState([]);
  const [selectedMovimientos, setSelectedMovimientos] = useState([]);
  const [numeroFactura, setNumeroFactura] = useState("");
  const [fechaFactura, setFechaFactura] = useState("");
  const [monto, setMonto] = useState("");
  const [tipoFactura, setTipoFactura] = useState("A");
  const [archivos, setArchivos] = useState([]);
  const { handleSnackbar } = useContext(MessageManager);

  const [GetProveedoresData, { data: proveedoresData }] = useLazyQuery(GET_PROVEEDORES);
  const [getMovimientos, { data: movimientosData, loading: loadingMovimientos }] =
    useLazyQuery(GET_MOVIMIENTOS);
  const [saveFactura, { loading: loadingSave }] = useMutation(SAVE_FACTURA);

  useEffect(() => {
    if (open) {
      GetProveedoresData();
      setProveedor(null);
      setMovimientos([]);
      setSelectedMovimientos([]);
      setNumeroFactura("");
      setFechaFactura("");
      setMonto("");
      setTipoFactura("A");
    }
  }, [open]);

  useEffect(() => {
    if (proveedor) {
      getMovimientos({
        variables: {
          filter: { idProveedor: proveedor, tipo: "Remito Proveedor", idFactura: null },
        },
      });
    }
  }, [proveedor]);

  useEffect(() => {
    if (movimientosData?.movimientos) {
      setMovimientos(movimientosData.movimientos);
    }
  }, [movimientosData]);

  const [openModalFiles, setOpenModalFiles] = useState(false);
  const handleCloseModalFiles = () => {
    setOpenModalFiles(false);
  };
  const handleOpenModalFiles = () => {
    setOpenModalFiles(true);
  };

  const calculateTotal = () => {
    return selectedMovimientos
      .map((id) => movimientos.find((mov) => mov.id === id))
      .reduce((total, mov) => {
        if (!mov) return total;
        const detallesTotal = mov.movimientoDetalles.reduce(
          (sum, detalle) =>
            sum +
            (detalle.cantidad / detalle.materiaPrima.tipoUnidad.multiplicador) * detalle.precio,
          0
        );
        return total + detallesTotal;
      }, 0);
  };

  const handleSave = () => {
    saveFactura({
      variables: {
        input: {
          idProveedor: proveedor,
          idMovimiento: selectedMovimientos,
          numeroFactura,
          fechaFactura,
          tipoFactura,
          monto: parseFloat(monto),
          files: archivos,
        },
      },
    })
      .then(() => {
        handleSnackbar("Factura guardada correctamente", "success");
        refetchParent();
        handleClose();
      })
      .catch((error) => {
        handleSnackbar(error.message || "Error al guardar la factura", "error");
      });
  };
  console.log("archivos", archivos);

  return (
    <Modal
      aria-labelledby="transition-modal-title"
      aria-describedby="transition-modal-description"
      open={open}
      onClose={handleClose}
      closeAfterTransition
    >
      <Fade in={open}>
        <Card sx={style}>
          <SoftBox display="flex" justifyContent="space-between">
            <SoftTypography variant="h6">Agregar Factura</SoftTypography>
            <Tooltip title="Cerrar" placement="top">
              <Icon fontSize="medium" onClick={handleClose} sx={{ cursor: "pointer" }}>
                close
              </Icon>
            </Tooltip>
          </SoftBox>

          <SoftBox>
            <Grid container spacing={2}>
              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="proveedor">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Proveedor
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  id="proveedor"
                  value={proveedor || ""}
                  onChange={(e) => setProveedor(e.target.value)}
                  fullWidth
                  disabled={selectedMovimientos.length > 0}
                >
                  <MenuItem value="" disabled>
                    Seleccione un proveedor
                  </MenuItem>
                  {proveedoresData?.proveedores?.map((prov) => (
                    <MenuItem key={prov.id} value={prov.id}>
                      {prov.razonSocial || `${prov.nombre} ${prov.apellido}`}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              <Grid item xs={12} sm={6}>
                <InputLabel htmlFor="numeroFactura">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Número de Factura
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="numeroFactura"
                  value={numeroFactura}
                  onChange={(e) => setNumeroFactura(e.target.value)}
                  placeholder="Ingrese el número de factura"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <InputLabel htmlFor="fechaFactura">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Fecha de Factura
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInput
                  id="fechaFactura"
                  type="date"
                  value={fechaFactura}
                  onChange={(e) => setFechaFactura(e.target.value)}
                />
              </Grid>
              <Grid item xs={12} sm={3}>
                <InputLabel htmlFor="tipoFactura">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Tipo de Factura
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  id="tipoFactura"
                  value={tipoFactura}
                  onChange={(e) => setTipoFactura(e.target.value)}
                  fullWidth
                >
                  <MenuItem value="A">Tipo A</MenuItem>
                  <MenuItem value="B">Tipo B</MenuItem>
                  <MenuItem value="C">Tipo C</MenuItem>
                </Select>
              </Grid>

              <Grid item xs={12} sm={3}>
                <InputLabel htmlFor="monto">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Monto
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <SoftInputNumber
                  id="monto"
                  value={monto}
                  onChange={(e) => setMonto(e.target.value)}
                  placeholder="Ingrese el monto"
                />
              </Grid>

              <Grid item xs={12} sm={3}>
                <SoftButton
                  sx={{
                    mt: 3.5,
                  }}
                  color="primary"
                  onClick={handleOpenModalFiles}
                  fullWidth
                >
                  Subir Archivos
                  <Icon>attach_file</Icon>
                </SoftButton>
              </Grid>

              <Grid item xs={12}>
                <InputLabel htmlFor="movimientos">
                  <SoftTypography component="label" variant="caption" fontWeight="bold">
                    Movimientos (Remito Proveedor)
                    <SoftTypography variant="caption" fontWeight="bold" color="primary">
                      &nbsp;*
                    </SoftTypography>
                  </SoftTypography>
                </InputLabel>
                <Select
                  id="movimientos"
                  multiple
                  value={selectedMovimientos}
                  onChange={(e) => setSelectedMovimientos(e.target.value)}
                  fullWidth
                  disabled={loadingMovimientos}
                  renderValue={(selected) => (
                    <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                      {selected.map((id) => {
                        const movimiento = movimientos.find((mov) => mov.id === id);
                        return (
                          <Chip
                            key={id}
                            label={
                              movimiento ? `#${movimiento.id} - ${movimiento.fechaEntrega}` : id
                            }
                          />
                        );
                      })}
                    </Box>
                  )}
                >
                  {movimientos.length === 0 && (
                    <MenuItem value="" disabled>
                      <SinDatos texto="No hay movimientos disponibles" />
                    </MenuItem>
                  )}

                  {movimientos.map((mov) => (
                    <MenuItem key={mov.id} value={mov.id}>
                      {`#${mov.id} - ${mov.fechaEntrega}`}
                    </MenuItem>
                  ))}
                </Select>
              </Grid>

              {selectedMovimientos.length > 0 && (
                <Grid item xs={12}>
                  <SoftTypography variant="h6" mt={2}>
                    Remitos Seleccionados
                  </SoftTypography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell align="center">
                            <strong>ID</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Fecha Entrega</strong>
                          </TableCell>
                          <TableCell align="center">
                            <strong>Total</strong>
                          </TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {selectedMovimientos.map((id) => {
                          const mov = movimientos.find((mov) => mov.id === id);
                          const total = mov.movimientoDetalles.reduce(
                            (sum, detalle) =>
                              sum +
                              (detalle.cantidad / detalle.materiaPrima.tipoUnidad.multiplicador) *
                                detalle.precio,
                            0
                          );
                          return (
                            <TableRow key={id}>
                              <TableCell align="center">{mov.id}</TableCell>
                              <TableCell align="center">{mov.fechaEntrega}</TableCell>
                              <TableCell align="center">{`$ ${formatMoneyPunto(total)}`}</TableCell>
                            </TableRow>
                          );
                        })}
                      </TableBody>
                    </Table>
                  </TableContainer>
                  <SoftTypography variant="h6" mt={2}>
                    Total General: ${formatMoneyPunto(calculateTotal())}
                  </SoftTypography>
                </Grid>
              )}
            </Grid>

            <SoftBox mt={4} display="flex" justifyContent="flex-end">
              <SoftButton color="primary" onClick={handleClose} circular sx={{ mr: 2 }}>
                <Icon>close</Icon>
                &nbsp;Cancelar
              </SoftButton>
              <SoftButton
                color="uproGreen"
                onClick={handleSave}
                circular
                disabled={
                  loadingSave ||
                  !proveedor ||
                  !numeroFactura ||
                  !fechaFactura ||
                  !monto ||
                  selectedMovimientos.length === 0
                }
              >
                {loadingSave ? (
                  <CircularProgress size={17} color="white" />
                ) : (
                  <>
                    Guardar &nbsp;
                    <Icon>save</Icon>
                  </>
                )}
              </SoftButton>
            </SoftBox>
          </SoftBox>
          <AddFiles
            open={openModalFiles}
            handleClose={handleCloseModalFiles}
            factura={archivos}
            handleSubmit={(files) => {
              setArchivos(files);
              handleCloseModalFiles();
            }}
          />
        </Card>
      </Fade>
    </Modal>
  );
}

ModalAddFactura.propTypes = {
  open: PropTypes.bool,
  handleClose: PropTypes.func,
  refetchParent: PropTypes.func,
};
